import { motion } from "framer-motion";
import { MapPin, Mail, Phone } from "lucide-react";

const t = {
  es: {
    label: "Cómo Llegar",
    title: "Encuéntranos en",
    titleAccent: "Monterrico",
    address: "Km 2.5 después del puente de Iztapa, carretera a Monterrico, Aldea Atitancito, Taxisco, Santa Rosa.",
    email: "berlisadepacifico@gmail.com",
    phone1: "+502 5199 5814",
    phone2: "+502 5032 8789",
    mapLabel: "Abrir en Google Maps",
  },
  en: {
    label: "How to Get Here",
    title: "Find us in",
    titleAccent: "Monterrico",
    address: "Km 2.5 past the Iztapa bridge, road to Monterrico, Aldea Atitancito, Taxisco, Santa Rosa.",
    email: "berlisadepacifico@gmail.com",
    phone1: "+502 5199 5814",
    phone2: "+502 5032 8789",
    mapLabel: "Open in Google Maps",
  },
};

export default function Contact({ lang }) {
  const tr = t[lang];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="max-w-2xl mx-auto text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-montserrat text-[#8C5E35] text-sm font-semibold tracking-widest uppercase mb-4"
          >
            {tr.label}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-montserrat font-extrabold text-[#1F3A5E] text-4xl md:text-5xl leading-tight"
            data-testid="contact-title"
          >
            {tr.title}{" "}
            <span className="text-[#8C5E35]">{tr.titleAccent}</span>
          </motion.h2>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div
              data-testid="contact-address"
              className="flex items-start gap-4 p-5 rounded-2xl bg-[#FAFAF9] border border-[#D4B895]/30"
            >
              <div className="w-10 h-10 rounded-xl bg-[#1F3A5E]/8 flex items-center justify-center shrink-0">
                <MapPin size={18} className="text-[#1F3A5E]" />
              </div>
              <div>
                <p className="font-montserrat font-semibold text-[#1F3A5E] text-sm mb-1">Ubicación</p>
                <p className="font-dm-sans text-gray-500 text-sm leading-relaxed">{tr.address}</p>
                <a
                  href="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d2302.596055027594!2d-90.68458286933138!3d13.929432248672729"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#8C5E35] text-xs font-semibold font-montserrat mt-2 inline-block hover:underline"
                >
                  {tr.mapLabel} →
                </a>
              </div>
            </div>

            <div
              data-testid="contact-email"
              className="flex items-start gap-4 p-5 rounded-2xl bg-[#FAFAF9] border border-[#D4B895]/30"
            >
              <div className="w-10 h-10 rounded-xl bg-[#1F3A5E]/8 flex items-center justify-center shrink-0">
                <Mail size={18} className="text-[#1F3A5E]" />
              </div>
              <div>
                <p className="font-montserrat font-semibold text-[#1F3A5E] text-sm mb-1">Email</p>
                <a
                  href={`mailto:${tr.email}`}
                  className="font-dm-sans text-gray-500 text-sm hover:text-[#8C5E35] transition-colors duration-200"
                >
                  {tr.email}
                </a>
              </div>
            </div>

            <div
              data-testid="contact-phone"
              className="flex items-start gap-4 p-5 rounded-2xl bg-[#FAFAF9] border border-[#D4B895]/30"
            >
              <div className="w-10 h-10 rounded-xl bg-[#1F3A5E]/8 flex items-center justify-center shrink-0">
                <Phone size={18} className="text-[#1F3A5E]" />
              </div>
              <div>
                <p className="font-montserrat font-semibold text-[#1F3A5E] text-sm mb-1">Teléfono</p>
                <a href="tel:+50251995814" className="font-dm-sans text-gray-500 text-sm block hover:text-[#8C5E35] transition-colors duration-200">{tr.phone1}</a>
                <a href="tel:+50250328789" className="font-dm-sans text-gray-500 text-sm block hover:text-[#8C5E35] transition-colors duration-200">{tr.phone2}</a>
              </div>
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-2 rounded-3xl overflow-hidden shadow-xl shadow-[#1F3A5E]/8 border border-[#D4B895]/20 bg-[#e5e8ec]"
            style={{ height: "380px" }}
            data-testid="contact-map"
          >
            <iframe
              style={{ border: 0, width: "100%", height: "100%" }}
              src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d2302.596055027594!2d-90.68458286933138!3d13.929432248672729!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1ses-419!2sgt!4v1680968045092!5m2!1ses-419!2sgt"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Berlisa del Pacífico Location"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
